import { z } from "zod";

// ── Domain schema (DB / server truth) ─────────────────────────────────────────

const JsonPrimitiveSchema = z.union([z.string(), z.number(), z.boolean(), z.null()]);
type JsonPrimitive = z.infer<typeof JsonPrimitiveSchema>;
type JsonValue = JsonPrimitive | { [key: string]: JsonValue } | JsonValue[];

const JsonValueSchema: z.ZodType<JsonValue> = z.lazy(() =>
  z.union([JsonPrimitiveSchema, z.array(JsonValueSchema), z.record(z.string(), JsonValueSchema)]),
);

export const WorksheetDataSchema = z
  .object({
    sheets: z.array(z.record(z.string(), JsonValueSchema)).default([]),
  })
  .catchall(JsonValueSchema);

export const WorksheetsSchema = z.object({
  id: z.string(),
  businessId: z.string(),
  name: z.string(),
  description: z.string().nullable().optional(),
  type: z.string().nullable().optional(),
  date: z.union([z.string(), z.date()]).nullable().optional(),
  data: WorksheetDataSchema,
  createdAt: z.union([z.string(), z.date()]),
  updatedAt: z.union([z.string(), z.date()]),
});

export type Worksheets = z.infer<typeof WorksheetsSchema>;

export const CreateWorksheetsSchema = WorksheetsSchema.omit({
  id: true,
  businessId: true,
  createdAt: true,
  updatedAt: true,
});
export const UpdateWorksheetsSchema = WorksheetsSchema.partial();

export type CreateWorksheetsInput = z.infer<typeof CreateWorksheetsSchema>;
export type UpdateWorksheetsInput = z.infer<typeof UpdateWorksheetsSchema>;

// ── Form schema (UI layer) ────────────────────────────────────────────────────

export const WorksheetsFormSchema = z.object({
  name: z.string().trim().min(1, "Name is required").max(255),
  description: z.string().max(1000).optional(),
  type: z.string().max(120).optional(),
  date: z.string().optional(),
  data: WorksheetDataSchema,
});

export type WorksheetsForm = z.infer<typeof WorksheetsFormSchema>;

// ── Mapping: Form → Domain ────────────────────────────────────────────────────

export function formToDomain(input: WorksheetsForm): UpdateWorksheetsInput {
  return {
    name: input.name,
    description: input.description || null,
    type: input.type || null,
    date: input.date ? new Date(input.date) : null,
    data: input.data,
  };
}

export function domainToForm(item: Worksheets): WorksheetsForm {
  return {
    name: item.name,
    description: item.description ?? "",
    type: item.type ?? "",
    date: item.date
      ? (item.date instanceof Date ? item.date : new Date(item.date))
          .toISOString()
          .slice(0, 10)
      : "",
    data: item.data,
  };
}
